import React, { createContext, useCallback, useContext, useState } from 'react';
import { DestinationMap, DestinationVariant, ExtendedDestinationModel } from '../types';

const PluginContext = createContext<ProviderProps>({
  destinationMaps: [],
  updateDestinationMap: () => {},
  isPluginEnabled: () => false,
  isDestinationDefined: () => false
});

interface ProviderProps {
  destinationMaps: DestinationMap[];
  updateDestinationMap: (plugin: DestinationMap) => void;
  isPluginEnabled: (pluginId: string) => boolean;
  isDestinationDefined: (destinationId: keyof DestinationMap, variant?: DestinationVariant) => boolean;
}

export function PluginProvider(props: React.PropsWithChildren) {
  const [destinationMaps, setDestinationMaps] = useState<DestinationMap[]>([]);

  const updateDestinationMap = useCallback((plugin: DestinationMap) => {
    setDestinationMaps((prevPlugins) => {
      const index = prevPlugins.findIndex((prevPlugin) => prevPlugin.id === plugin.id);
      return index === -1
        ? [...prevPlugins, plugin]
        : prevPlugins.map((prevPlugin) => (prevPlugin.id === plugin.id ? plugin : prevPlugin));
    });
  }, []);

  const isPluginEnabled = useCallback(
    (pluginId: string) => !!destinationMaps.filter((plugin) => plugin.id === pluginId).length,
    [destinationMaps]
  );

  const isDestinationDefined = useCallback(
    (destinationId: keyof DestinationMap, variant?: DestinationVariant) =>
      destinationMaps.some((plugin) => {
        if (variant) {
          return (
            Object.keys(plugin).includes(destinationId) &&
            (plugin[destinationId] as ExtendedDestinationModel)?.variant === variant
          );
        }
        return (
          Object.keys(plugin).includes(destinationId) && !(plugin[destinationId] as ExtendedDestinationModel).variant
        );
      }),
    [destinationMaps]
  );

  return (
    <PluginContext.Provider
      value={{
        updateDestinationMap,
        destinationMaps,
        isPluginEnabled,
        isDestinationDefined
      }}
      {...props}
    />
  );
}

export function usePluginContext() {
  return useContext(PluginContext);
}
